import {
  ActionIcon,
  Box,
  Button,
  createStyles,
  Flex,
  Modal,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import { uploadFileToS3Api } from "../../../../utils/PresignedUrlFunction";
import { useSelector } from "react-redux";
import { useMediaQuery } from "@mantine/hooks";
import { encrypt, showError, showSuccess } from "../../../../utils";
import { FiEdit } from "react-icons/fi";
import { TbKeyboard } from "react-icons/tb";
import { CiImageOn } from "react-icons/ci";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IoIosCloseCircle } from "react-icons/io";
import {
  createSignature,
  updateSignature,
} from "../../../../services/signature";

const useStyles = createStyles((theme) => ({
  createBtn: {
    padding: "12px 20px",
    borderRadius: "8px",
    border: "1px solid #FFAF4A",
    backgroundColor: "#FFAF4A",
    height: 44,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#262626",
    ":hover": {
      backgroundColor: "#FFAF4A",
      opacity: 0.8,
    },
  },
  cancelBtn: {
    padding: "0px 16px",
    borderRadius: "8px",
    border: "1px solid #D0D5DD",
    backgroundColor: "#F9FAFB",
    height: 38,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#262626",
    ":hover": {
      backgroundColor: "#F9FAFB",
      opacity: 0.8,
    },
  },
  createSignModal: {
    "& .mantine-Modal-body": {
      backgroundColor: "#FFF",
    },
    "& .mantine-Modal-content": {
      backgroundColor: "#FFF",
    },
    "& .mantine-Modal-header": {
      backgroundColor: "#FFF",
    },
    "& .mantine-Modal-close": {
      color: "#000000",
      width: "24px",
      height: "24px",
      "&:hover": {
        backgroundColor: "#FFF",
      },
    },
  },
  inputField: {
    "& .mantine-InputWrapper-label": {
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "Inter",
      color: "#404040",
      marginBottom: 6,
    },
    "& .mantine-Input-wrapper": {
      padding: "10px 14px",
      height: 44,
      borderRadius: 8,
      border: "1px solid #D0D5DD",
      background: "transparent",
      boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
      "& input, & .mantine-PasswordInput-input": {
        height: "100%",
        border: "none",
        padding: "0px",
        background: "transparent",
        minHeight: "unset",
        fontSize: 16,
        fontWeight: 400,
        fontFamily: "Inter",
        color: "#171717",

        "&:-webkit-autofill": {
          boxShadow: "0 0 0px 1000px transparent inset !important",
          WebkitTextFillColor: "#171717",
          transition: "background-color 5000s ease-in-out 0s",
        },

        "&::placeholder": {
          color: "#737373",
        },
      },
    },
  },
  tabs: {
    width: "100%",
    fontFamily: "Inter",
  },
  tab_list: {
    background: "#EAECF0",
    borderRadius: "8px 8px 0px 0px",
    border: "none",
    justifyContent: "center",
  },
  tabPanel: {},
  tab: {
    color: "#979797 !important",
    fontFamily: "Inter",
    fontSize: 14,
    marginBottom: 0,
    padding: "20.2px 16px 20.2px 10px",
    fontWeight: 500,
    borderWidth: "2px !important",
    "& > span": {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    "&:hover": {
      color: "#27272A !important",
      background: "transparent",
    },
    "&[data-active]": {
      color: "#27272A !important",
      borderColor: "#27272A",
      "&:hover": {
        borderColor: "#27272A",
      },
    },
  },

  signaturePadParent: {
    position: "relative",
    width: "100%",
    display: "inline-block",
    "& canvas": {
      border: "0.5px solid #CBD5E1",
      background: "#fff",
      cursor: "crosshair",
    },
  },
  textPad: {
    height: 160,
    backgroundColor: "#FFF",
    border: "0.5px solid #CBD5E1",
    padding: 10,
    "& input": {
      fontFamily: "'Island Moments', cursive",
      fontSize: 64,
      height: "unset",
      fontWeight: 400,
      color: "#344054",
      textAlign: "center",
    },
  },
  uploadBox: {
    height: 160,
    width: "100%",
    border: "0.5px solid #CBD5E1",
    backgroundColor: "#FFF",
    padding: "0px",
    "& .uploadBoxInner": {
      height: "100%",
      width: "100%",
    },
  },
  previewBox: {
    padding: 10,
    minHeight: 160,
    width: "100%",
    border: "0.5px solid #CBD5E1",
    backgroundColor: "#FFF",
  },
  previewImg: {
    display: "block",
    objectFit: "contain",
    maxHeight: 200,
    width: "auto",
    margin: "auto",
  },
  selectedImgClose: {
    position: "absolute",
    top: 6,
    right: 6,
    borderRadius: "50%",
    backgroundColor: "#27272A",
    paddingLeft: 1,
    paddingBottom: 0.5,
  },
}));

export const CreateSignatureModal = ({ opened, onClose }) => {
  const { signature, signatureImage, signatureText } = useSelector(
    (state) => state?.signature
  );
  const [currentTab, setCurrentTab] = useState("draw");
  const signatureCanvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const initialsCanvasRef = useRef(null);
  const initialsPadRef = useRef(null);
  const { classes } = useStyles();
  const { profile } = useSelector((state) => state?.profile);
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const [error, setError] = useState({
    signature: "",
    initials: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const isEdit = signature || signatureImage || signatureText;
  const [signatureTextValue, setSignatureTextValue] = useState("");
  const [initialsTextValue, setInitialsTextValue] = useState("");

  useEffect(() => {
    if (!opened) return;

    const checkCanvas = () => {
      const initialsCanvas = initialsCanvasRef.current;

      if (initialsCanvas) {
        initialsPadRef.current?.off();
        initialsCanvas.width = isMobileScreen ? 300 : 620;
        initialsCanvas.height = 160;
        initialsPadRef.current = new SignaturePad(initialsCanvas, {
          backgroundColor: "rgba(255,255,255,0)",
          penColor: "rgb(0,0,0)",
        });
      } else {
        setTimeout(checkCanvas, 50);
      }
    };

    checkCanvas();
  }, [opened, currentTab]);

  useEffect(() => {
    if (!opened) return;

    const checkCanvas = () => {
      const canvas = signatureCanvasRef.current;

      if (canvas) {
        signaturePadRef.current?.off(); // remove old listeners
        canvas.width = isMobileScreen ? 300 : 620;
        canvas.height = 160;
        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: "rgba(255,255,255,0)",
          penColor: "rgb(0,0,0)",
        });
      } else {
        setTimeout(checkCanvas, 50);
      }
    };

    checkCanvas();
  }, [opened]);

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
    initialsPadRef.current?.clear();
    setSignatureTextValue("");
    setSelectedImage(null);
    setInitialsTextValue("");
  };

  const handleModalClose = () => {
    setLoading(false);
    setError({
      signature: "",
      initials: "",
    });
    onClose();
    setSelectedImage(null);
    signaturePadRef.current?.clear();
    setSignatureTextValue("");
    initialsPadRef.current?.clear();
    setInitialsTextValue("");
  };

  // function getTrimmedCanvas(pad) {
  //   const canvas = pad.canvas;
  //   const ctx = canvas.getContext("2d");
  //   const w = canvas.width;
  //   const h = canvas.height;
  //   const imageData = ctx.getImageData(0, 0, w, h);
  //   const data = imageData.data;

  //   let top = h,
  //     left = w,
  //     right = 0,
  //     bottom = 0;

  //   for (let y = 0; y < h; y++) {
  //     for (let x = 0; x < w; x++) {
  //       const idx = (y * w + x) * 4;
  //       const alpha = data[idx + 3]; // alpha channel
  //       if (alpha > 0) {
  //         // non-transparent pixel
  //         if (x < left) left = x;
  //         if (x > right) right = x;
  //         if (y < top) top = y;
  //         if (y > bottom) bottom = y;
  //       }
  //     }
  //   }

  //   if (right < left || bottom < top) {
  //     // empty canvas
  //     return canvas;
  //   }

  //   const trimWidth = right - left;
  //   const trimHeight = bottom - top;

  //   const trimmedCanvas = document.createElement("canvas");
  //   trimmedCanvas.width = trimWidth;
  //   trimmedCanvas.height = trimHeight;
  //   const trimmedCtx = trimmedCanvas.getContext("2d");

  //   trimmedCtx.drawImage(
  //     canvas,
  //     left,
  //     top,
  //     trimWidth,
  //     trimHeight,
  //     0,
  //     0,
  //     trimWidth,
  //     trimHeight
  //   );

  //   return trimmedCanvas;
  // }

  const handleSaveSignature = async () => {
    const hasDrawing =
      signaturePadRef.current && !signaturePadRef.current.isEmpty();
    const hasInitialsDrawing =
      initialsPadRef.current && !initialsPadRef.current.isEmpty();
    const hasImage = !!selectedImage;
    const hasSignatureText = !!signatureTextValue;
    const hasInitialsText = !!initialsTextValue;

    let errors = {
      signature: "",
      initials: "",
    };

    if (!hasDrawing && !hasImage && !hasSignatureText) {
      errors.signature = "Please provide a signature.";
    }

    if (!hasInitialsDrawing && !hasInitialsText) {
      errors.initials = "Please provide initials.";
    }

    if (errors.signature || errors.initials) {
      setError(errors);
      return;
    }

    try {
      setLoading(true);
      let payload = {};

      if (hasDrawing) {
        const dataUrl = signaturePadRef.current.toDataURL("image/png");
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const fileToUpload = new File([blob], `signature_${Date.now()}.png`, {
          type: "image/png",
        });

        const result = await uploadFileToS3Api({ file: fileToUpload });

        payload = {
          signature: encrypt(result?.uploadURL),
          signedAt: new Date().toISOString(),
          signatureActive: true,
          signatureText: null,
          signatureImage: null,
        };
      } else if (hasImage) {
        const result = await uploadFileToS3Api({ file: selectedImage });
        payload = {
          signatureImage: encrypt(result?.uploadURL),
          signedAt: new Date().toISOString(),
          signatureActive: true,
          signatureText: null,
          signature: null,
        };
      } else if (hasSignatureText) {
        payload = {
          signatureText: signatureTextValue,
          signedAt: new Date().toISOString(),
          signatureActive: true,
          signature: null,
          signatureImage: null,
        };
      }

      if (hasInitialsDrawing) {
        const initialsDataUrl = initialsPadRef.current.toDataURL("image/png");
        const initialsRes = await fetch(initialsDataUrl);
        const initialsBlob = await initialsRes.blob();
        const initialsFileToUpload = new File(
          [initialsBlob],
          `initials_${Date.now()}.png`,
          { type: "image/png" }
        );

        const initialsResult = await uploadFileToS3Api({
          file: initialsFileToUpload,
        });
        payload = {
          ...payload,
          signatureInitialsText: null,
          signatureInitials: encrypt(initialsResult?.uploadURL),
        };
      } else if (hasInitialsText) {
        payload = {
          ...payload,
          signatureInitials: null,
          signatureInitialsText: initialsTextValue,
        };
      }

      if (isEdit) {
        const response = await updateSignature({
          ...payload,
          signatureId: signature?._id,
        });
        if (response?.status === 200) {
          showSuccess(response?.data?.message);
        } else {
          showError(response?.data?.message);
        }
      } else {
        await createSignature(payload);
        showSuccess("Signature saved successfully");
      }
      handleModalClose();
    } catch (error) {
      handleModalClose();
      setLoading(false);
      console.error(error);
    }
  };

  const getInitials = () => {
    if (profile?.user?.firstName && profile?.user?.lastName) {
      // If both firstName and lastName exist
      return (
        (profile.user.firstName.charAt(0) || "") +
        (profile.user.lastName.charAt(0) || "")
      );
    } else if (profile?.user?.name) {
      // If only name exists, split and get first letters of first two words
      const nameParts = profile.user.name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        return nameParts[0].charAt(0) + nameParts[1].charAt(0);
      } else if (nameParts.length === 1) {
        // If only one word, return first character
        return nameParts[0].charAt(0);
      }
    }
    return ""; // Default fallback
  };

  const handleTabChange = (value) => {
    if (currentTab === "draw") {
      setSignatureTextValue("");
      setSelectedImage(null);
      setInitialsTextValue("");
      setCurrentTab(value);
    } else if (currentTab === "type") {
      signaturePadRef.current?.clear();
      setSelectedImage(null);
      setCurrentTab(value);
    } else {
      signaturePadRef.current?.clear();
      setSignatureTextValue("");
      setInitialsTextValue("");
      setCurrentTab(value);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      centered
      padding={20}
      size="xl"
      radius="lg"
      zIndex={1000}
      bg="red"
      transitionProps={{ duration: 200 }}
      className={classes.createSignModal}
    >
      <Box px={54}>
        <Flex justify="flex-end" mb={5}>
          <Button className={classes.cancelBtn} onClick={handleClearSignature}>
            Clear
          </Button>
        </Flex>
        <Flex gap={46}>
          <TextInput
            className={classes.inputField}
            label="Full Name"
            size="lg"
            w="70%"
            readOnly
            value={profile?.user?.name || ""}
          />
          <TextInput
            className={classes.inputField}
            label="Initials"
            size="lg"
            w="30%"
            value={getInitials()}
            readOnly
          />
        </Flex>
        <Flex direction="column" align="center" mt={30}>
          <Tabs
            className={classes.tabs}
            defaultValue={"draw"}
            value={currentTab}
            onTabChange={handleTabChange}
          >
            <Tabs.List justify="center" className={classes.tab_list}>
              <Tabs.Tab className={classes.tab} value="draw" c={"#979797"}>
                <FiEdit size={20} /> Draw
              </Tabs.Tab>
              <Tabs.Tab className={classes.tab} value="type" c={"#979797"}>
                <TbKeyboard size={25} /> Type
              </Tabs.Tab>
              <Tabs.Tab className={classes.tab} value="image" c={"#979797"}>
                <CiImageOn size={25} /> Image
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="draw" className={classes.tabPanel}>
              <div className={classes.signaturePadParent}>
                <canvas ref={signatureCanvasRef} />
                {error.signature != "" ? (
                  <Text fs={14} fw={500} color="red">
                    {error.signature}
                  </Text>
                ) : (
                  ""
                )}
              </div>
            </Tabs.Panel>
            <Tabs.Panel value="type" className={classes.tabPanel}>
              <Flex align="center" justify="center" className={classes.textPad}>
                <TextInput
                  variant="unstyled"
                  value={signatureTextValue}
                  onChange={(event) =>
                    setSignatureTextValue(event.currentTarget.value)
                  }
                />
              </Flex>
              {error.signature != "" ? (
                <Text fs={14} fw={500} color="red">
                  {error.signature}
                </Text>
              ) : (
                ""
              )}
            </Tabs.Panel>
            <Tabs.Panel value="image" className={classes.tabPanel}>
              <Box>
                {selectedImage ? (
                  <Flex
                    align="center"
                    justify="center"
                    className={classes.previewBox}
                  >
                    <Box pos="relative" display="inline-block">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="preview"
                        className={classes.previewImg}
                      />

                      <ActionIcon
                        variant="transparent"
                        className={classes.selectedImgClose}
                        onClick={() => setSelectedImage(null)}
                      >
                        <IoIosCloseCircle size={24} color="#FFF" />
                      </ActionIcon>
                    </Box>
                  </Flex>
                ) : (
                  <Dropzone
                    accept={IMAGE_MIME_TYPE}
                    onDrop={(files) => setSelectedImage(files[0])}
                    maxSize={2 * 1024 ** 2}
                    multiple={false}
                    classNames={{
                      root: classes.uploadBox,
                      inner: "uploadBoxInner",
                    }}
                  >
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      h="100%"
                      w="100%"
                    >
                      <Text fz={16} fw={600} color="#171717" ff="Inter">
                        <Text
                          span
                          td="underline"
                          fz={16}
                          fw={600}
                          color="#171717"
                          ff="Inter"
                        >
                          Click to Upload
                        </Text>{" "}
                        or Drag and Drop
                      </Text>
                      <Text fz={12} fw={500} c="#979797" ff="Inter" mt={8}>
                        Maximum File Size 2MB
                      </Text>
                    </Flex>
                  </Dropzone>
                )}
              </Box>
              {error.signature != "" ? (
                <Text fs={14} fw={500} color="red">
                  {error.signature}
                </Text>
              ) : (
                ""
              )}
            </Tabs.Panel>
          </Tabs>
          <Box mt={12} w="100%">
            <Text fz={14} ff="Inter" color="#404040" fw={500} mb={6}>
              Draw Initials
            </Text>
            {currentTab === "type" ? (
              <>
                <Flex
                  align="center"
                  justify="center"
                  className={classes.textPad}
                  w="100%"
                >
                  <TextInput
                    variant="unstyled"
                    value={initialsTextValue}
                    onChange={(event) =>
                      setInitialsTextValue(event.currentTarget.value)
                    }
                  />
                </Flex>
                {error.initials != "" ? (
                  <Text fs={14} fw={500} color="red">
                    {error.initials}
                  </Text>
                ) : (
                  ""
                )}
              </>
            ) : (
              <div className={classes.signaturePadParent}>
                <canvas ref={initialsCanvasRef} />
                {error.initials != "" ? (
                  <Text fs={14} fw={500} color="red">
                    {error.initials}
                  </Text>
                ) : (
                  ""
                )}
              </div>
            )}
          </Box>
          <Flex justify="end" w="100%" my={10}>
            <Button
              className={classes.createBtn}
              disabled={loading}
              // w={157}
              size="lg"
              onClick={handleSaveSignature}
              loading={loading}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Modal>
  );
};
